import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Clock,
  Users,
  Settings,
  Database,
  Rocket,
  AlertTriangle,
  ArrowRight,
  Calendar,
  Target,
} from 'lucide-react';

interface TimelinePhase {
  id: string;
  name: string;
  description: string;
  duration: string;
  startMonth: number;
  endMonth: number;
  icon: React.ReactNode;
  deliverables: string[];
  dependencies?: string[];
  customerInvolvement: string;
  riskLevel: 'low' | 'medium' | 'high';
}

interface CommunityComplexity {
  type: 'simple' | 'moderate' | 'complex' | 'enterprise';
  label: string;
  description: string;
  indicators: string[];
  baseMonths: number;
  bufferMonths: number;
  riskFactors: string[];
}

export function ImplementationTimeline() {
  const [selectedComplexity, setSelectedComplexity] = useState<
    'moderate' | 'simple' | 'complex' | 'enterprise'
  >('moderate');
  const navigate = useNavigate();

  const complexityTypes: CommunityComplexity[] = [
    {
      type: 'simple',
      label: 'Simple Implementation',
      description: 'New community or minimal existing systems',
      indicators: [
        'New community formation (no legacy systems)',
        'Basic feature requirements only',
        'No custom integrations needed',
        'Data in Excel/simple formats',
        'Single decision maker or small board',
        'No compliance requirements beyond basics',
      ],
      baseMonths: 4,
      bufferMonths: 1,
      riskFactors: ['Scope creep during implementation', 'User adoption challenges'],
    },
    {
      type: 'moderate',
      label: 'Standard Implementation',
      description: 'Established community with some existing systems',
      indicators: [
        'Existing QuickBooks or similar accounting software',
        '1-3 systems requiring integration',
        'Standard feature set with minor customizations',
        'Moderate data migration (spreadsheets + some databases)',
        'Normal board/committee decision processes',
        'Basic compliance needs (HOA, state regulations)',
      ],
      baseMonths: 6,
      bufferMonths: 2,
      riskFactors: ['Integration complexity', 'Data quality issues', 'Change management'],
    },
    {
      type: 'complex',
      label: 'Complex Implementation',
      description: 'Multiple legacy systems and advanced requirements',
      indicators: [
        'Multiple existing systems (accounting, property mgmt, etc)',
        '4-8 required integrations',
        'Custom feature requirements',
        'Complex data migration (multiple databases/formats)',
        'Legacy system dependencies',
        'Extended approval processes',
        'Compliance beyond standard (PCI, SOX, etc)',
      ],
      baseMonths: 6,
      bufferMonths: 2,
      riskFactors: [
        'Legacy system limitations',
        'Data migration complexity',
        'Integration failures',
        'Extended testing phases',
      ],
    },
    {
      type: 'enterprise',
      label: 'Enterprise Implementation',
      description: 'Highly complex systems and custom requirements',
      indicators: [
        'Extensive legacy system ecosystem',
        '8+ complex integrations required',
        'Significant custom development needs',
        'Multi-database, multi-format data migration',
        'Security/compliance requirements (SOX, PCI, etc)',
        'Multiple stakeholder groups with complex approval chains',
        'Custom API development required',
        'High-volume transaction processing needs',
      ],
      baseMonths: 6,
      bufferMonths: 2,
      riskFactors: [
        'Legacy system constraints',
        'Regulatory compliance',
        'Multi-system integration risks',
        'Extensive testing requirements',
        'Complex stakeholder alignment',
      ],
    },
  ];

  const getTimelinePhases = (complexity: CommunityComplexity): TimelinePhase[] => [
    {
      id: 'planning',
      name: 'Planning & Preparation',
      description: 'Project kickoff, requirements gathering, and resource allocation',
      duration: '1 month',
      startMonth: 1,
      endMonth: 1,
      icon: <Target className="w-5 h-5" />,
      deliverables: [
        'Project plan and timeline',
        'Resource allocation',
        'Risk mitigation plan',
        'Communication strategy',
      ],
      customerInvolvement: 'High - Kickoff meeting, requirements review',
      riskLevel: 'low',
    },
    {
      id: 'frontend',
      name: 'Front-End Development',
      description: 'UI/UX implementation, module activation, role configuration',
      duration: '1 month',
      startMonth: 2,
      endMonth: 2,
      icon: <Settings className="w-5 h-5" />,
      deliverables: [
        'Web app interface built with branding',
        'User role configuration complete',
        'Workflow implementation (Gold)',
        'Custom domain setup',
      ],
      dependencies: ['planning'],
      customerInvolvement: 'Medium - Bi-weekly check-ins, prototype testing',
      riskLevel: 'medium',
    },
    {
      id: 'backend',
      name: 'Back-End Development & Database Architecture',
      description: 'Database design, API development, cloud infrastructure deployment',
      duration: '1 month',
      startMonth: 3,
      endMonth: 3,
      icon: <Database className="w-5 h-5" />,
      deliverables: [
        'Database schema tailored to community',
        'API endpoints connecting front-end to database',
        'Security implementation (RBAC, encryption)',
        'Cloud infrastructure deployed',
      ],
      dependencies: ['frontend'],
      customerInvolvement: 'Low - Provide financial records and governing documents',
      riskLevel: 'medium',
    },
    {
      id: 'security',
      name: 'Internal QA & Hardening',
      description: 'Automated test suite, dependency vulnerability scanning, and internal review',
      duration: '1 month',
      startMonth: 4,
      endMonth: 4,
      icon: <Settings className="w-5 h-5" />,
      deliverables: [
        'Automated test suite run against the build',
        'Dependency vulnerability scan (npm audit)',
        'Internal security review by the development team',
        'Bug fixes from QA findings',
      ],
      dependencies: ['backend'],
      customerInvolvement: 'None - Internal QA process',
      riskLevel: 'low',
    },
    {
      id: 'migration',
      name: 'Data Integration & Migration',
      description: 'Import governing documents, resident data, and financial records',
      duration: '1 month',
      startMonth: 5,
      endMonth: 5,
      icon: <Database className="w-5 h-5" />,
      deliverables: [
        'Governing documents loaded and organized',
        'Resident data migrated and validated',
        'Administrator accounts created',
        'Data integrity checks passed',
      ],
      dependencies: ['security'],
      customerInvolvement: 'High - Finalize resident data, review imported data',
      riskLevel: 'medium',
    },
    {
      id: 'training',
      name: 'Training, UAT & Launch Preparation',
      description: 'Administrator training, user acceptance testing, soft launch',
      duration: '1 month',
      startMonth: 6,
      endMonth: 6,
      icon: <Users className="w-5 h-5" />,
      deliverables: [
        'Administrator training (up to 8 hours)',
        'Training materials delivered (videos, guides)',
        'UAT completed with admins',
        'Platform approved for launch',
      ],
      dependencies: ['migration'],
      customerInvolvement: 'High - Attend training, conduct UAT, plan rollout',
      riskLevel: 'low',
    },
    {
      id: 'golive',
      name: 'Go-Live & Stabilization',
      description: 'Public launch and 30-day heightened support period',
      duration: '30 days post-onboarding',
      startMonth: 7,
      endMonth: 7,
      icon: <Rocket className="w-5 h-5" />,
      deliverables: [
        'Platform launches to all residents',
        'Heightened support (30 days)',
        'Performance monitoring',
        'Ongoing Service Agreement begins',
      ],
      dependencies: ['training'],
      customerInvolvement: 'Medium - Distribute signup links, resident onboarding',
      riskLevel: 'medium',
    },
  ];

  const currentComplexity = complexityTypes.find((c) => c.type === selectedComplexity)!;
  const phases = getTimelinePhases(currentComplexity);
  const totalMonths = currentComplexity.baseMonths;
  const totalWithBuffer = totalMonths + currentComplexity.bufferMonths;

  const getRiskColor = (risk: 'low' | 'medium' | 'high') => {
    switch (risk) {
      case 'low':
        return 'bg-green-100 text-green-800 hover:bg-green-100 hover:text-green-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100 hover:text-yellow-800';
      case 'high':
        return 'bg-red-100 text-red-800 hover:bg-red-100 hover:text-red-800';
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold">Implementation Timeline</h2>
        <p className="text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base px-2 sm:px-4 md:px-0">
          Your implementation timeline varies based on community complexity and existing systems.
          Select your community type below to see a customized timeline.
        </p>
      </div>

      {/* Complexity Selector */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-2 sm:px-0">
        {complexityTypes.map((complexity) => (
          <Card
            key={complexity.type}
            className={`cursor-pointer transition-all border-2 ${
              selectedComplexity === complexity.type
                ? 'border-blue-500 shadow-lg'
                : 'hover:border-blue-200'
            }`}
            onClick={() => setSelectedComplexity(complexity.type)}
          >
            <CardHeader className="pb-3 px-4 sm:px-6">
              <CardTitle className="text-base sm:text-lg flex items-center justify-between">
                {complexity.label}
                {selectedComplexity === complexity.type && (
                  <CheckCircle className="w-5 h-5 text-blue-600" />
                )}
              </CardTitle>
              <p className="text-xs sm:text-sm text-muted-foreground">{complexity.description}</p>
            </CardHeader>
            <CardContent className="pt-0 px-4 sm:px-6">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs sm:text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                    <span>
                      {complexity.baseMonths}-{complexity.baseMonths + complexity.bufferMonths}{' '}
                      months
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Timeline Summary */}
      <Card className="bg-linear-to-r from-blue-50 to-purple-50 border-blue-200 mx-2 sm:mx-0">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
            <div>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold">
                {currentComplexity.label} Timeline
              </h3>
              <p className="text-muted-foreground text-xs sm:text-sm md:text-base">
                Estimated implementation duration
              </p>
            </div>
            <div className="text-left sm:text-right">
              <div className="text-lg sm:text-xl md:text-2xl font-bold text-blue-600">
                {totalMonths}-{totalWithBuffer} months
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground">Including buffer time</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 text-center">
            <div className="p-2 sm:p-3 bg-white rounded-lg">
              <div className="font-semibold text-sm sm:text-base">Start to Finish</div>
              <div className="text-xs sm:text-sm text-muted-foreground">
                {totalMonths} months baseline
              </div>
            </div>
            <div className="p-2 sm:p-3 bg-white rounded-lg">
              <div className="font-semibold text-sm sm:text-base">Buffer Time</div>
              <div className="text-xs sm:text-sm text-muted-foreground">
                {currentComplexity.bufferMonths} months contingency
              </div>
            </div>
            <div className="p-2 sm:p-3 bg-white rounded-lg">
              <div className="font-semibold text-sm sm:text-base">Go-Live Target</div>
              <div className="text-xs sm:text-sm text-muted-foreground">
                Month {totalMonths + 1}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Phase Timeline */}
      <div className="space-y-4 px-2 sm:px-0">
        <h3 className="text-xl sm:text-2xl font-semibold text-center">Implementation Phases</h3>

        {phases.map((phase, index) => (
          <Card key={phase.id} className="relative">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start gap-2 sm:gap-4">
                {/* Phase Icon */}
                <div className="shrink-0">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    {phase.icon}
                  </div>
                  {index < phases.length - 1 && (
                    <div className="w-0.5 h-12 sm:h-16 bg-gray-200 ml-5 sm:ml-6 mt-2"></div>
                  )}
                </div>

                {/* Phase Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 space-y-2 sm:space-y-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                      <h4 className="text-base sm:text-lg font-semibold">{phase.name}</h4>
                      <Badge
                        className={getRiskColor(phase.riskLevel) + ' text-xs whitespace-nowrap'}
                      >
                        {phase.riskLevel} risk
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-muted-foreground whitespace-nowrap">
                      <Calendar className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>
                        Month {phase.startMonth}
                        {phase.startMonth !== phase.endMonth ? `-${phase.endMonth}` : ''}
                      </span>
                      <span className="mx-1 sm:mx-2">•</span>
                      <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                      <span>{phase.duration}</span>
                    </div>
                  </div>

                  <p className="text-muted-foreground mb-4 text-sm sm:text-base">
                    {phase.description}
                  </p>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <div>
                      <h5 className="font-medium mb-2 text-sm sm:text-base">Key Deliverables</h5>
                      <ul className="space-y-1">
                        {phase.deliverables.map((deliverable, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs sm:text-sm">
                            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-green-600 mt-0.5 shrink-0" />
                            <span>{deliverable}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h5 className="font-medium mb-2 text-sm sm:text-base">
                        Customer Involvement
                      </h5>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        {phase.customerInvolvement}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Risk Factors */}
      <Card className="bg-amber-50 border-amber-200 mx-2 sm:mx-0">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
            Factors That Can Affect Timeline
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <h5 className="font-medium mb-3 text-sm sm:text-base">Potential Delays</h5>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li>• Legacy system complexities</li>
                <li>• Data quality issues requiring cleanup</li>
                <li>• Third-party integration challenges</li>
                <li>• Extended approval processes</li>
                <li>• Resource availability conflicts</li>
              </ul>
            </div>

            <div>
              <h5 className="font-medium mb-3 text-sm sm:text-base">Acceleration Opportunities</h5>
              <ul className="space-y-2 text-xs sm:text-sm">
                <li>• Pre-cleaned, organized data</li>
                <li>• Dedicated customer project team</li>
                <li>• Minimal custom requirements</li>
                <li>• Fast decision-making processes</li>
                <li>• Parallel training and setup activities</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* CTA */}
      <div className="text-center space-y-4 pt-8 px-2 sm:px-4 md:px-0">
        <h3 className="text-base sm:text-lg md:text-xl font-semibold">
          Ready to Start Your Implementation?
        </h3>
        <p className="text-muted-foreground mb-6 text-xs sm:text-sm md:text-base max-w-2xl mx-auto">
          Get a customized timeline based on your community's specific needs and requirements.
        </p>
        <div className="flex justify-center gap-4">
          <Button
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-sm sm:text-base px-4 sm:px-8"
            onClick={() => navigate('/schedule-demo')}
          >
            Schedule Demo
            <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
